import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ClipboardCheck, 
  MessageSquare, 
  FolderKanban, 
  FileText,
  Receipt,
  Clock,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Users,
  Headphones,
  Phone,
  Mail
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Link } from 'react-router-dom';
import api from '../../lib/axios';

const CustomerServiceDashboard = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urgentTickets, setUrgentTickets] = useState([]);
  const [clientsToContact, setClientsToContact] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchUrgentData();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard/customer-service');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUrgentData = async () => {
    try {
      // Fetch urgent tickets
      const ticketsRes = await api.get('/tickets/', { params: { status: 'open' } });
      const urgent = (ticketsRes.data || [])
        .filter(t => t.priority === 'urgent' || t.priority === 'high')
        .slice(0, 5);
      setUrgentTickets(urgent);
      
      // Fetch clients with pending briefs or validations
      const projectsRes = await api.get('/projects/', { params: { status: 'brief_pending' } });
      const clientIds = [...new Set((projectsRes.data || []).map(p => p.client_id).filter(Boolean))];
      
      if (clientIds.length > 0) {
        const clientsRes = await api.get('/users/', { params: { role: 'client' } });
        const clients = (clientsRes.data || []).filter(c => clientIds.includes(c.id));
        setClientsToContact(clients.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching urgent data:', error);
    }
  };

  const stats = [
    {
      title: 'Validations en attente',
      value: dashboard?.pending_validations || 0,
      icon: ClipboardCheck,
      color: 'bg-amber-500',
      link: '/projects?status=review',
      description: 'Projets à valider par le client'
    },
    {
      title: 'Demandes de devis',
      value: dashboard?.pending_quotes || 0,
      icon: FileText,
      color: 'bg-blue-500',
      link: '/prospects',
      description: 'Nouvelles demandes'
    },
    {
      title: 'Projets actifs',
      value: dashboard?.active_projects || 0,
      icon: FolderKanban,
      color: 'bg-emerald-500',
      link: '/projects',
      description: 'En cours de production'
    },
    {
      title: 'Tickets ouverts',
      value: dashboard?.open_tickets || 0,
      icon: MessageSquare,
      color: 'bg-rose-500',
      link: '/tickets',
      description: 'Demandes de support'
    },
    {
      title: 'Factures créatifs',
      value: dashboard?.pending_invoices || 0,
      icon: Receipt,
      color: 'bg-violet-500',
      link: '/invoices',
      description: 'En attente de validation'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'project': return FolderKanban;
      case 'ticket': return MessageSquare;
      case 'quote': return FileText;
      case 'invoice': return Receipt;
      default: return Clock;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                 style={{ backgroundColor: theme.colors?.accent || '#00d4aa' }}>
              <Headphones className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Bonjour {user?.first_name} ! 👋
              </h1>
              <p className="text-slate-500">
                Service Client • Vue d'ensemble
              </p>
            </div>
          </div>
          
          {/* Motivational Message */}
          {dashboard && (
            <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-100">
              <p className="text-slate-700">
                {(() => {
                  const totalTasks = (dashboard.pending_validations || 0) + 
                                    (dashboard.pending_quotes || 0) + 
                                    (dashboard.open_tickets || 0) + 
                                    (dashboard.pending_invoices || 0);
                  
                  if (totalTasks === 0) {
                    return (
                      <>
                        <span className="font-medium text-emerald-600">Félicitations ! 🎉</span> Tu as tout géré, il n'y a rien en attente. Profite de ce moment de calme !
                      </>
                    );
                  }
                  
                  const tasks = [];
                  if (dashboard.pending_validations > 0) tasks.push(`${dashboard.pending_validations} validation${dashboard.pending_validations > 1 ? 's' : ''} client`);
                  if (dashboard.pending_quotes > 0) tasks.push(`${dashboard.pending_quotes} demande${dashboard.pending_quotes > 1 ? 's' : ''} de devis`);
                  if (dashboard.open_tickets > 0) tasks.push(`${dashboard.open_tickets} ticket${dashboard.open_tickets > 1 ? 's' : ''} à traiter`);
                  if (dashboard.pending_invoices > 0) tasks.push(`${dashboard.pending_invoices} facture${dashboard.pending_invoices > 1 ? 's' : ''} à contrôler`);
                  
                  return (
                    <>
                      Ta journée va être {totalTasks > 5 ? 'bien remplie' : 'productive'} car il y a <span className="font-semibold text-pink-600">{tasks.join(', ')}</span> à gérer. <span className="font-medium">Bon courage à toi ! 💪</span>
                    </>
                  );
                })()}
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" 
                 style={{ borderColor: theme.colors?.primary }}></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Link 
                    key={index} 
                    to={stat.link}
                    className="block"
                  >
                    <Card className="hover:shadow-lg transition-all cursor-pointer border-0 shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className={cn("p-2 rounded-lg", stat.color)}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          {stat.value > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {stat.value}
                            </Badge>
                          )}
                        </div>
                        <div className="text-2xl font-bold mb-1" style={{ color: theme.colors?.primary }}>
                          {stat.value}
                        </div>
                        <div className="text-sm font-medium text-slate-700">{stat.title}</div>
                        <div className="text-xs text-slate-400 mt-1">{stat.description}</div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {/* Quick Actions & Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Actions rapides
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/projects?status=review">
                    <Button variant="outline" className="w-full justify-between group">
                      <span className="flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4" />
                        Voir les validations
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  </Link>
                  <Link to="/prospects">
                    <Button variant="outline" className="w-full justify-between group">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Gérer les prospects
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  </Link>
                  <Link to="/tickets">
                    <Button variant="outline" className="w-full justify-between group">
                      <span className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Traiter les tickets
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  </Link>
                  <Link to="/clients">
                    <Button variant="outline" className="w-full justify-between group">
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Voir tous les clients
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border-0 shadow-sm lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Activité récente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboard?.recent_activity && dashboard.recent_activity.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_activity.map((activity, index) => {
                        const Icon = getActivityIcon(activity.type);
                        return (
                          <Link
                            key={index}
                            to={activity.link}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="p-2 rounded-full bg-slate-100">
                              <Icon className="w-4 h-4 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate" style={{ color: theme.colors?.primary }}>
                                {activity.title}
                              </div>
                              <div className="text-xs text-slate-500">{activity.description}</div>
                            </div>
                            <div className="text-xs text-slate-400 whitespace-nowrap">
                              {formatDate(activity.timestamp)}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune activité récente</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Priority Alerts */}
            {(dashboard?.pending_validations > 0 || dashboard?.open_tickets > 0) && (
              <Card className="mt-6 border-l-4 border-l-amber-500 bg-amber-50/50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-amber-800">Actions prioritaires</div>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1">
                        {dashboard?.pending_validations > 0 && (
                          <li>• {dashboard.pending_validations} projet(s) en attente de validation client</li>
                        )}
                        {dashboard?.open_tickets > 0 && (
                          <li>• {dashboard.open_tickets} ticket(s) de support à traiter</li>
                        )}
                        {dashboard?.pending_quotes > 0 && (
                          <li>• {dashboard.pending_quotes} demande(s) de devis à traiter</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Urgent Tickets & Clients to Contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Urgent Tickets */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-rose-500" />
                    Tickets urgents
                    {urgentTickets.length > 0 && (
                      <Badge className="bg-rose-100 text-rose-700 ml-2">{urgentTickets.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {urgentTickets.length > 0 ? (
                    <div className="space-y-3">
                      {urgentTickets.map(ticket => (
                        <Link 
                          key={ticket.id} 
                          to={`/tickets/${ticket.id}`}
                          className="flex items-center justify-between p-3 rounded-lg bg-rose-50 hover:bg-rose-100 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{ticket.subject}</p>
                            <p className="text-xs text-slate-500">{ticket.user_name || 'Client'}</p>
                          </div>
                          <Badge className={cn(
                            "ml-2 shrink-0",
                            ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                          )}>
                            {ticket.priority === 'urgent' ? 'Urgent' : 'Haute'}
                          </Badge>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                      <p className="text-sm">Aucun ticket urgent</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Clients to Contact */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Phone className="w-5 h-5 text-blue-500" />
                    Clients à contacter
                    {clientsToContact.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 ml-2">{clientsToContact.length}</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>Briefs en attente de signature</CardDescription>
                </CardHeader>
                <CardContent>
                  {clientsToContact.length > 0 ? (
                    <div className="space-y-3">
                      {clientsToContact.map(client => (
                        <div 
                          key={client.id} 
                          className="flex items-center justify-between p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">
                              {client.company_name || `${client.first_name} ${client.last_name}`}
                            </p>
                            <p className="text-xs text-slate-500">{client.email}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {client.phone && (
                              <a 
                                href={`tel:${client.phone}`}
                                className="p-2 rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                                title="Appeler"
                              >
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                            <a 
                              href={`mailto:${client.email}`}
                              className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                              title="Envoyer un email"
                            >
                              <Mail className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                      <p className="text-sm">Tous les briefs sont signés</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default CustomerServiceDashboard;
